import React from "react";
import {ProcessedTourOperator} from "./contentfulModel.ts";

export interface RichTextNode {
    type: string;
    content?: RichTextNode[];
    text?: string;
    marks?: Array<{ type: string }>;
}

export const getTickIcon = (color: string) => (
    <svg width="18" height="13" viewBox="0 0 18 13" fill="none" xmlns="http://www.w3.org/2000/svg">
<path
    d="M7.33702 12.6191C6.83476 13.127 6.0231 13.127 5.52084 12.6191L0.376575 7.41827C-0.125525 6.91038 -0.125525 6.08962 0.376575 5.58173C0.878754 5.07384 1.69282 5.07384 2.19508 5.58173L6.39277 9.86021L15.8071 0.380919C16.3094 -0.126973 17.121 -0.126973 17.6233 0.380919C18.1256 0.888811 18.1256 1.70956 17.6233 2.21746L7.33702 12.6191Z"
fill={color} />
</svg>
);
export const renderRichText = (tick: React.ReactNode, node: RichTextNode) => {
    if (!node) return null;

    //if node is an array, map through it
    if (Array.isArray(node)) {
        return node.map((n, index) => renderRichText(tick, n));
    }

    if (node.type === 'document') {
        return (
            <div className="rich-text">
                {node.content?.map((contentNode, index) => renderRichText(tick, contentNode))}
                </div>
        );
    }

    if (node.type === 'paragraph') {
        return (
            <p key={`p-${Math.random().toString(36).substring(2)}`} className="text-sm leading-6 mb-3">
            {node.content?.map((textNode, index) => renderRichText(tick, textNode))}
            </p>
    );
    }

    if (node.type === 'unordered-list') {
        return (
            <ul key={`ul-${Math.random().toString(36).substring(2)}`} className="mb-4">
            {node.content?.map((textNode, index) => renderRichText(tick, textNode))}
            </ul>
    );
    }

    if (node.type === 'list-item') {
        return (
            <li key={`li-${Math.random().toString(36).substring(2)}`} className="mb-3 flex items-start gap-3">
        <div className="flex-shrink-0 mt-1">
            {tick}
            </div>
            <div className="flex-grow">
            {node.text}
            </div>
            </li>
    );
    }

    if (node.type === 'text') {
        let content = node.text || '';

        if (node.marks && node.marks.length > 0) {
            node.marks.forEach((mark) => {
                const key = `text-${Math.random().toString(36).substring(2)}`;
                switch (mark.type) {
                    case 'bold':
                        content = <strong key={key}>{content}</strong>;
                        break;
                    case 'italic':
                        content = <em key={key}>{content}</em>;
                        break;
                    case 'underline':
                        content = <u key={key}>{content}</u>;
                        break;
                    default:
                        break;
                }
            });
        }

        return content;
    }

    return null;
};

export const processFonts = (tourOperator: ProcessedTourOperator) => {
    // URL de la fuente proporcionada por el usuario, o una por defecto
    const headingFontUrl = tourOperator.headingFontUrl || '';
    const textFontUrl = tourOperator.textFontUrl || '';

    // Nombres por defecto en caso de que no existan fuentes
    const defaultHeadingFont = 'Arial, sans-serif';
    const defaultTextFont = 'Arial, sans-serif';

    // Extraer el nombre de la fuente desde la URL
    const getFontFamilyFromUrl = (fontUrl) => {
        const match = fontUrl.match(/family=([^:&]+)/);
        return match ? match[1].replace(/\+/g, ' ') : null;
    };

    const headingFontFamily = getFontFamilyFromUrl(headingFontUrl) || defaultHeadingFont;
    const textFontFamily = getFontFamilyFromUrl(textFontUrl) || defaultTextFont;

    // Cargar las fuentes dinámicamente si las URLs están presentes
    const loadFont = (url: string) => {
        if (!url) return;
        const existingLink = document.querySelector(`link[href="${url}"]`);
        if (existingLink) return; // evitar duplicados

        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = url;
        document.head.appendChild(link);
    };

    loadFont(headingFontUrl);
    loadFont(textFontUrl);

    // Crear variables CSS con los nombres de las fuentes
    const style = document.createElement('style');
    style.innerHTML = `
    :root {
      --heading-font: '${headingFontFamily}', ${defaultHeadingFont};
      --text-font: '${textFontFamily}', ${defaultTextFont};
    }
  `;
    document.head.appendChild(style);
};


// Catálogo MICE: fichas de una sola ubicación (no viajes por días), con pestañas renombradas
// y subcategorías en orden fijo. Se activa con `type: "MICE"` en el catálogo.
export const isMiceCatalog = (tourCatalog?: { type?: string }) =>
    tourCatalog?.type?.trim().toUpperCase() === "MICE";

const MICE_SUBCATEGORY_ORDER = ["hoteles", "campamentos", "restaurantes", "venues"];

// Orden fijo de subcategorías en MICE; lo no listado va al final, A→Z.
export const compareMiceSubcategories = (a: string, b: string) => {
    const rank = (s: string) => {
        const i = MICE_SUBCATEGORY_ORDER.indexOf(s.trim().toLowerCase());
        return i === -1 ? MICE_SUBCATEGORY_ORDER.length : i;
    };
    return rank(a) - rank(b) || a.localeCompare(b);
};

// "4* · Gueliz · 85 hab." → { stars: "4★", rest: ["Gueliz", "85 hab."] }
export const parseMiceSubtitle = (subtitle = '') => {
    const parts = subtitle.split('·').map(s => s.trim()).filter(Boolean);
    const starIdx = parts.findIndex(p => /^\d\s*\*+$/.test(p));
    return {
        stars: starIdx >= 0 ? parts[starIdx].replace(/\s*\*+$/, '★') : undefined,
        rest: parts.filter((_, i) => i !== starIdx),
    };
};

// Texto legible sobre un color de fondo hex: oscuro sobre colores claros, blanco sobre oscuros
export const textColorOn = (hex: string, dark = '#1D1B18') => {
    const m = hex.replace('#', '').match(/^([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})/i);
    if (!m) return dark;
    const [r, g, b] = m.slice(1).map(c => {
        const v = parseInt(c, 16) / 255;
        return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
    });
    const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
    // Umbral donde el contraste con blanco y con el tono oscuro se iguala
    return luminance > 0.18 ? dark : '#FFFFFF';
};

// Pide al CDN de Cloudflare la imagen a un ancho concreto en lugar del original.
// Solo afecta a URLs que ya pasan por /cdn-cgi/image/; el resto se devuelve igual.
export const cdnImageWidth = (url: string | undefined, width: number) => {
    if (!url) return url;
    return url.replace(/\/cdn-cgi\/image\/([^/]+)\//, (_, opts: string) => {
        const rest = opts.split(',').filter(o => !o.startsWith('width=') && !o.startsWith('w='));
        return `/cdn-cgi/image/${[...rest, `width=${width}`].join(',')}/`;
    });
};

// Clave de LocationIQ para el callejero de MICE (.env.[mode].local). Sin ella se usa el mapa de país.
export const LOCATIONIQ_KEY: string | undefined = import.meta.env.VITE_LOCATIONIQ_KEY;
